import { useCallback, useEffect, useMemo, useState } from "react";
import type { CatalogItem } from "@/lib/airtable";
import {
  type InitialPreferences,
  type SwipeState,
  canUndo as canUndoState,
  createInitialState,
  reduce,
  selectCurrentItem,
  selectRemainingCount,
} from "@/lib/swipeRecommender";

export type { InitialPreferences } from "@/lib/swipeRecommender";

export const useSwipeRecommender = (items: CatalogItem[], initialPreferences?: InitialPreferences) => {
  const [state, setState] = useState<SwipeState>(() => createInitialState(items, initialPreferences));

  useEffect(() => {
    setState(createInitialState(items, initialPreferences));
  }, [items, initialPreferences]);

  const currentItem = useMemo(() => selectCurrentItem(state, items), [items, state]);
  const remainingCount = useMemo(() => selectRemainingCount(state, items), [items, state]);

  const like = useCallback(() => setState((prev) => reduce(prev, { type: "like" }, items)), [items]);
  const dislike = useCallback(() => setState((prev) => reduce(prev, { type: "dislike" }, items)), [items]);
  const undo = useCallback(() => setState((prev) => reduce(prev, { type: "undo" }, items)), [items]);
  const reset = useCallback(() => setState((prev) => reduce(prev, { type: "reset" }, items)), [items]);

  return {
    currentItem,
    like,
    dislike,
    undo,
    reset,
    remainingCount,
    canUndo: canUndoState(state),
    likedItems: state.liked,
    dislikedItems: state.disliked
  };
};
